import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2, Lock, Pencil, PieChart, Plus, Split, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { splitHref } from "@/lib/splits";
import { toast } from "sonner";
import { offerTax } from "@/pages/TaxFolder";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, errMsg, fieldErrs, statusOf } from "@/lib/api";
import { fmtDate, money, monthLabel, shiftMonth, todayIn } from "@/lib/format";
import { SPENDING_CATEGORIES, slug } from "@/lib/constants";
import { useHub } from "@/components/AppShell";
import { EmptyState, Field, Loader, NoAccess, PageHeader } from "@/components/common";
import { useLiveRefresh } from "@/lib/live";

const GREEN = "var(--glow-money)";

function ExpenseForm({ hubId, initial, today, onSaved, onCancel }) {
  const [f, setF] = useState({ amount: initial?.amount ?? "", category: initial?.category || "Groceries", date: initial?.date || today, note: initial?.note || "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => { setF({ ...f, [k]: e.target.value }); setErrors({ ...errors, [k]: "" }); };
  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (f.amount === "") errs.amount = "Add the amount";
    else if (!(Number(f.amount) > 0)) errs.amount = "Amount must be more than $0";
    if (!f.category) errs.category = "Pick a category";
    if (!f.date) errs.date = "Add the date";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      const r = initial?.id ? await api.put(`/organizations/${hubId}/spending/${initial.id}`, f) : await api.post(`/organizations/${hubId}/spending`, f);
      onSaved(f);
      if (!initial?.id) offerTax(hubId, { spending_id: r.data.id }, r.data.tax_suggestion);
    } catch (err) {
      fieldErrs(err) ? setErrors(fieldErrs(err)) : toast.error(errMsg(err));
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4" noValidate data-testid="expense-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Amount (USD)" htmlFor="ex-amount" error={errors.amount} errorTestid="expense-amount-error">
          <input id="ex-amount" type="number" inputMode="decimal" step="0.01" min="0" className="q-input" placeholder="0.00" value={f.amount} onChange={set("amount")} aria-invalid={!!errors.amount} data-testid="expense-amount-input" />
        </Field>
        <Field label="Date" htmlFor="ex-date" error={errors.date} errorTestid="expense-date-error">
          <input id="ex-date" type="date" className="q-input" value={f.date} onChange={set("date")} aria-invalid={!!errors.date} data-testid="expense-date-input" />
        </Field>
      </div>
      <Field label="Category" htmlFor="ex-cat" error={errors.category} errorTestid="expense-category-error">
        <select id="ex-cat" className="q-input" value={f.category} onChange={set("category")} data-testid="expense-category-select">
          {SPENDING_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </Field>
      <Field label="Note (optional)" htmlFor="ex-note">
        <input id="ex-note" className="q-input" placeholder="e.g. Trader Joe's" value={f.note} onChange={set("note")} data-testid="expense-note-input" />
      </Field>
      <div className="flex justify-end gap-2 pt-2">
        <button type="button" className="q-btn q-btn-ghost" onClick={onCancel} data-testid="expense-cancel-btn">Cancel</button>
        <button className="q-btn q-btn-primary" disabled={busy} data-testid="expense-save-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Save</button>
      </div>
    </form>
  );
}

function CategoryBars({ data }) {
  const max = Math.max(...data.by_category.map((c) => c.total), 1);
  if (!data.by_category.length) return <p className="text-sm text-q-faint">No spending this month yet.</p>;
  return (
    <ul className="space-y-3.5" data-testid="spending-categories">
      {data.by_category.map((c, i) => (
        <li key={c.category} data-testid={`spending-cat-${slug(c.category)}`}>
          <div className="mb-1.5 flex justify-between text-sm">
            <span className="font-semibold text-q-text">{c.category}</span>
            <span className="font-display font-bold tabular-nums text-q-text" data-testid={`spending-cat-amount-${slug(c.category)}`}>{money(c.total)}</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-q-raised">
            <div className="h-full rounded-full transition-[width] duration-700 ease-out" style={{ width: `${(c.total / max) * 100}%`, background: `rgba(${GREEN},${1 - Math.min(i, 6) * 0.1})`, boxShadow: `0 0 10px rgba(${GREEN},0.5)` }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

function MonthPicker({ month, setMonth }) {
  return (
    <div className="flex items-center gap-1 rounded-xl border border-q-line bg-q-surface p-1" data-testid="spending-month-picker">
      <button className="q-btn q-btn-ghost px-2 py-2" onClick={() => setMonth(shiftMonth(month, -1))} aria-label="Previous month" data-testid="spending-month-prev"><ChevronLeft className="h-4 w-4" /></button>
      <span className="min-w-[130px] text-center font-display text-sm font-bold text-q-text" data-testid="spending-month-label">{monthLabel(month)}</span>
      <button className="q-btn q-btn-ghost px-2 py-2" onClick={() => setMonth(shiftMonth(month, 1))} aria-label="Next month" data-testid="spending-month-next"><ChevronRight className="h-4 w-4" /></button>
      <input type="month" className="w-[42px] cursor-pointer bg-transparent text-transparent outline-none" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} aria-label="Pick a month" data-testid="spending-month-input" />
    </div>
  );
}

export default function Spending() {
  const { hub } = useHub();
  const today = todayIn(hub.timezone);
  const [month, setMonth] = useState(today.slice(0, 7));
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);
  const [form, setForm] = useState(null);
  const [del, setDel] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/organizations/${hub.id}/spending`, { params: { month } });
      setData(data);
    } catch (e) { if (statusOf(e) === 403) setDenied(true); }
  }, [hub.id, month]);
  useEffect(() => { load(); }, [load]);
  useLiveRefresh(() => load(), (m) => ["spending", "bill"].includes(m.entity), null);

  if (denied) return <NoAccess />;

  const remove = async () => {
    try {
      await api.delete(`/organizations/${hub.id}/spending/${del.id}`);
      toast.success("Expense deleted");
      setDel(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="spending-page">
      <PageHeader eyebrow="Money" title="Spending" rgb={GREEN} testid="spending-title" actions={<>
        <MonthPicker month={month} setMonth={setMonth} />
        <button className="q-btn q-btn-primary" disabled={hub.read_only} onClick={() => setForm({})} data-testid="spending-add-btn"><Plus className="h-4 w-4" /> Add expense</button>
      </>} />
      {!data || data.month !== month ? <Loader /> : (
        <div className="grid gap-6 lg:grid-cols-[1fr_1.5fr]">
          <div className="space-y-6">
            <section className="q-card" style={{ background: `radial-gradient(circle at 0% 0%, rgba(${GREEN},0.14), transparent 65%), rgb(var(--q-surface))` }}>
              <div className="text-[10px] font-black uppercase tracking-[0.18em]" style={{ color: `rgb(${GREEN})` }}>Spent in {monthLabel(month)}</div>
              <div className="mt-3 font-display text-4xl font-extrabold tracking-tight text-q-text" data-testid="spending-total">{money(data.total)}</div>
              <div className="mt-1 text-xs text-q-faint">{data.entries.filter((s) => !s.income).length} {data.entries.filter((s) => !s.income).length === 1 ? "expense" : "expenses"}</div>
              {data.income > 0 && <div className="mt-2 text-sm font-semibold text-emerald-300" data-testid="spending-income-total">+{money(data.income)} reimbursed</div>}
            </section>
            <section className="q-card">
              <h2 className="mb-5 font-display text-lg font-bold text-q-text">By category</h2>
              <CategoryBars data={data} />
            </section>
          </div>
          <section>
            {data.entries.length === 0 ? (
              <EmptyState Icon={PieChart} rgb={GREEN} testid="spending-empty" title="Nothing spent this month"
                hint="Log an expense, or mark a bill paid and it shows up here." action={<button className="q-btn q-btn-primary" onClick={() => setForm({})}>Add expense</button>} />
            ) : (
              <ul className="divide-y divide-q-border overflow-hidden rounded-xl border border-q-border bg-q-surface" data-testid="spending-list">
                {data.entries.map((s) => (
                  <li key={s.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3.5 sm:px-5" data-testid={`spending-row-${s.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate font-semibold text-q-text">{s.note || s.category}</span>
                        {s.from_bill && <span className="rounded px-1.5 py-0.5 text-[9px] font-black uppercase tracking-[0.12em]" style={{ color: `rgb(${GREEN})`, background: `rgba(${GREEN},0.12)` }} data-testid={`spending-from-bill-${s.id}`}>From bill</span>}
                      </div>
                      <div className="mt-0.5 text-xs text-q-faint">{s.category} · {fmtDate(s.date)}</div>
                    </div>
                    <span className={`font-display font-bold tabular-nums ${s.income ? "text-emerald-300" : "text-q-text"}`} data-testid={`spending-amount-${s.id}`}>{s.income ? `+${money(s.amount)}` : money(s.amount)}</span>
                    {s.income && <span className="rounded px-1.5 py-0.5 text-[9px] font-black uppercase tracking-[0.12em] text-emerald-300" style={{ background: "rgba(var(--st-emerald),0.12)" }} data-testid={`spending-income-${s.id}`}>Income</span>}
                    {hub.is_parent && !hub.read_only && !s.income && <Link to={splitHref(hub.id, s.note || s.category, s.amount, s.date)} className="q-btn q-btn-ghost px-2 py-1.5 text-xs" title="Split this" data-testid={`spending-split-btn-${s.id}`}><Split className="h-3.5 w-3.5" /> Split</Link>}
                    {s.linked ? (
                      <Link to={`/h/${hub.id}/${s.linked.path}`} className="flex min-h-[44px] items-center gap-1 text-[11px] text-q-faint underline" data-testid={`spending-linked-${s.id}`}><Lock className="h-3 w-3" /> Change this from {s.linked.label}</Link>
                    ) : s.from_bill ? (
                      <span className="flex items-center gap-1 text-[11px] text-q-faint" title="Change this from the bill" data-testid={`spending-locked-${s.id}`}>
                        <button className="q-btn q-btn-ghost px-2 py-1.5" disabled title="Change this from the bill" data-testid={`spending-edit-btn-${s.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                        <button className="q-btn q-btn-ghost px-2 py-1.5" disabled title="Change this from the bill" data-testid={`spending-delete-btn-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                        <Lock className="h-3 w-3" /> Change this from the bill
                      </span>
                    ) : (
                      <span className="flex items-center gap-1">
                        <button className="q-btn q-btn-ghost px-2 py-1.5" disabled={hub.read_only} onClick={() => setForm(s)} title="Edit" data-testid={`spending-edit-btn-${s.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                        <button className="q-btn q-btn-ghost px-2 py-1.5 hover:text-red-300" disabled={hub.read_only} onClick={() => setDel(s)} title="Delete" data-testid={`spending-delete-btn-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="border-q-line bg-q-surface sm:max-w-lg" data-testid="expense-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-extrabold">{form?.id ? "Edit expense" : "Add an expense"}</DialogTitle>
            <DialogDescription className="text-q-muted">Amounts are in USD.</DialogDescription>
          </DialogHeader>
          {form && <ExpenseForm hubId={hub.id} initial={form} today={today} onCancel={() => setForm(null)}
            onSaved={(f) => { toast.success("Expense saved"); setForm(null); f.date.slice(0, 7) === month ? load() : setMonth(f.date.slice(0, 7)); }} />}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent className="border-q-line bg-q-surface" data-testid="expense-delete-dialog">
          <AlertDialogHeader><AlertDialogTitle className="font-display">Delete this expense?</AlertDialogTitle></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="expense-delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 text-white hover:bg-red-500" data-testid="expense-delete-confirm-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
